import * as APITypes from "~/API";

const LEASE_STATUSES = new Set<string>(
  Object.values(APITypes.LeaseStatusType),
);

function trimId(value: string | null): string | undefined {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
}

function parseOptionalInt(
  value: string | null,
  min: number,
  max: number,
): number | undefined {
  if (value == null || value === "") return undefined;
  const parsed = Number.parseInt(value, 10);
  if (!Number.isFinite(parsed) || parsed < min || parsed > max) {
    return undefined;
  }
  return parsed;
}

function parseStatuses(
  searchParams: URLSearchParams,
): APITypes.LeaseStatusType[] | undefined {
  const statusesRaw = searchParams.get("statuses");
  if (statusesRaw) {
    const parsed = statusesRaw
      .split(",")
      .map((value) => value.trim())
      .filter((value) => LEASE_STATUSES.has(value)) as APITypes.LeaseStatusType[];
    return parsed.length > 0 ? parsed : undefined;
  }

  const statusRaw = searchParams.get("status");
  if (statusRaw && LEASE_STATUSES.has(statusRaw)) {
    return [statusRaw as APITypes.LeaseStatusType];
  }

  return undefined;
}

export type LeaseListFiltersType = {
  /** @deprecated Prefer statuses */
  status?: APITypes.LeaseStatusType;
  statuses?: APITypes.LeaseStatusType[];
  unitID?: string;
  tenantID?: string;
  startWithinMonths?: number;
  endWithinMonths?: number;
  pastActive?: boolean;
};

export function parseLeaseListFilters(
  searchParams: URLSearchParams,
): LeaseListFiltersType {
  const filters: LeaseListFiltersType = {};

  const statuses = parseStatuses(searchParams);
  if (statuses) {
    filters.statuses = statuses;
    if (statuses.length === 1) {
      filters.status = statuses[0];
    }
  }

  const unitID = trimId(searchParams.get("unitID"));
  if (unitID) filters.unitID = unitID;

  const tenantID = trimId(searchParams.get("tenantID"));
  if (tenantID) filters.tenantID = tenantID;

  const startWithinMonths = parseOptionalInt(
    searchParams.get("startWithinMonths"),
    1,
    24,
  );
  if (startWithinMonths !== undefined) {
    filters.startWithinMonths = startWithinMonths;
  }

  const endWithinMonths = parseOptionalInt(
    searchParams.get("endWithinMonths"),
    1,
    24,
  );
  if (endWithinMonths !== undefined) {
    filters.endWithinMonths = endWithinMonths;
  }

  const pastActiveRaw = searchParams.get("pastActive");
  if (pastActiveRaw === "true" || pastActiveRaw === "1") {
    filters.pastActive = true;
  }

  return filters;
}

export function leaseFiltersToLoaderParams(
  filters: LeaseListFiltersType,
  base: { ownerID: string; ownerType: APITypes.OwnerType },
): Record<string, string | undefined> {
  return {
    ownerID: base.ownerID,
    ownerType: base.ownerType,
    statuses:
      filters.statuses && filters.statuses.length > 0
        ? filters.statuses.join(",")
        : undefined,
    unitID: filters.unitID,
    tenantID: filters.tenantID,
    startWithinMonths:
      filters.startWithinMonths === undefined
        ? undefined
        : String(filters.startWithinMonths),
    endWithinMonths:
      filters.endWithinMonths === undefined
        ? undefined
        : String(filters.endWithinMonths),
    pastActive: filters.pastActive ? "true" : undefined,
  };
}
